import {
  isInPool,
  onPlatform,
  SALES_VERTICALS,
  type PerformancePeriod,
  type SalesSubject,
  type SalesVertical,
} from '@/client/types/sales';
import {
  hitMatchesSubject,
  inferActivity,
  MARKETPLACE_HITS,
  type ListingActivity,
  type MarketplaceHit,
  type MarketplaceId,
} from './marketplace-catalog';

export type ShareSiteId =
  | MarketplaceId
  | 'feelyfeely'
  | 'ghanaweb'
  | 'realtor'
  | 'privateproperty';
export type SharePeriod = PerformancePeriod;

export const SHARE_SITES: ShareSiteId[] = [
  'feelyfeely',
  'jiji',
  'meqasa',
  'gpc',
  'lamudi',
  'seventerra',
  'ghanaweb',
  'realtor',
  'privateproperty',
];

export const SHARE_PERIODS: SharePeriod[] = ['7d', '30d', '90d', 'all'];

export const SHARE_PERIOD_LABEL: Record<SharePeriod, string> = {
  '7d': '7 days',
  '30d': '30 days',
  '90d': '90 days',
  all: 'All time',
};

export const SHARE_SITE_LABEL: Record<ShareSiteId, string> = {
  feelyfeely: 'FeelyFeely',
  jiji: 'Jiji',
  meqasa: 'meQasa',
  gpc: 'GPC',
  lamudi: 'Lamudi',
  seventerra: '7th Terra',
  ghanaweb: 'GhanaWeb',
  realtor: 'Realtor',
  privateproperty: 'Private Property',
};

/** Dominant color sampled from each site’s icon (7th Terra has no usable mark). */
export const SHARE_SITE_COLOR: Record<ShareSiteId, string> = {
  feelyfeely: '#0C24C0',
  jiji: '#00B43C',
  meqasa: '#FC546C',
  gpc: '#CC2424',
  lamudi: '#3060B4',
  seventerra: '#9A3412',
  ghanaweb: '#1D4ED8',
  realtor: '#0F766E',
  privateproperty: '#E31C5C',
};

const VERTICAL_CATEGORY: Record<SalesVertical, string> = {
  homes: 'Houses',
  market: 'Electronics',
  locals: 'Health',
  places: 'Short stay',
};

export interface SiteShareRow {
  id: ShareSiteId;
  label: string;
  sellers: number;
  liveListings: number;
  postedListings: number;
  allTimePosted: number;
  paid: number;
  free: number;
  liveInventoryGhs: number;
  postedInventoryGhs: number;
  stillLive: number | null;
  promoGhs: number;
  inPool: number;
  alsoOnFf: number;
}

export interface CategoryShareRow {
  category: string;
  bySite: Record<ShareSiteId, number>;
  total: number;
}

export interface DeskShareRow {
  vertical: SalesVertical;
  ffSellers: number;
  competitorSellers: number;
  ffListings: number;
  competitorListings: number;
}

export interface ProspectShare {
  period: SharePeriod;
  sites: SiteShareRow[];
  categories: CategoryShareRow[];
  desks: DeskShareRow[];
  competitorSellers: number;
  overlap: number;
  payingElsewhere: number;
  payingCaptured: number;
  listingTotal: number;
  liveListingTotal: number;
  liveInventoryTotal: number;
  sellerTotal: number;
  ffListingShare: number;
  ffLiveInventoryShare: number;
  ffSellerShare: number;
  captureRate: number;
}

function ffCategories(subject: SalesSubject): string[] {
  if (subject.activity.listingCategories?.length) return subject.activity.listingCategories;
  return [...new Set(subject.verticals.map((v) => VERTICAL_CATEGORY[v]))];
}

function emptyCounts(): Omit<SiteShareRow, 'id' | 'label'> {
  return {
    sellers: 0,
    liveListings: 0,
    postedListings: 0,
    allTimePosted: 0,
    paid: 0,
    free: 0,
    liveInventoryGhs: 0,
    postedInventoryGhs: 0,
    stillLive: null,
    promoGhs: 0,
    inPool: 0,
    alsoOnFf: 0,
  };
}

function matchVertical(verticals: SalesVertical[], vertical: SalesVertical | 'all'): boolean {
  return vertical === 'all' || verticals.includes(vertical);
}

function ym(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function sumHistory(activity: ListingActivity, fromKey?: string, toKey?: string): number {
  return activity.history
    .filter((p) => (!fromKey || p.at >= fromKey) && (!toKey || p.at <= toKey))
    .reduce((n, p) => n + p.count, 0);
}

function postedListings(activity: ListingActivity, period: SharePeriod, atMs: number): number {
  if (period === '7d') return activity.listingsLast7;
  if (period === '30d') return activity.listingsLast30;
  if (period === 'all') return sumHistory(activity);
  return sumHistory(activity, ym(atMs - 90 * 86400_000), ym(atMs));
}

function allTimePosted(activity: ListingActivity, liveListings: number): number {
  return Math.max(sumHistory(activity), liveListings);
}

function promoInPeriod(monthlyGhs: number, lifetimeGhs: number, period: SharePeriod): number {
  if (period === 'all') return lifetimeGhs || monthlyGhs;
  if (period === '7d') return Math.round((monthlyGhs * 7) / 30);
  if (period === '90d') return monthlyGhs * 3;
  return monthlyGhs;
}

function subjectActivity(subject: SalesSubject): ListingActivity {
  const live = Math.max(subject.platform?.listingCount ?? 0, subject.activity.listingCount ?? 0);
  const inferred = inferActivity(live, subject.activity.listedSince ?? subject.createdAt.slice(0, 4));
  const last30 = subject.activity.listingsLast30;
  if (last30 == null) return inferred;
  return {
    ...inferred,
    listingsLast30: last30,
    listingsLast7: Math.min(last30, inferred.listingsLast7),
  };
}

function stillLiveRatio(liveListings: number, allTime: number): number | null {
  if (!allTime) return null;
  return liveListings / allTime;
}

function addSite(
  site: SiteShareRow,
  input: {
    liveListings: number;
    postedListings: number;
    allTimePosted: number;
    liveInventoryGhs: number;
    avgPriceGhs: number;
    paid: boolean;
    promoGhs: number;
    inPool: boolean;
  },
) {
  site.sellers += 1;
  site.liveListings += input.liveListings;
  site.postedListings += input.postedListings;
  site.allTimePosted += input.allTimePosted;
  if (input.paid) site.paid += 1;
  else site.free += 1;
  site.liveInventoryGhs += input.liveInventoryGhs;
  site.postedInventoryGhs += Math.round(input.avgPriceGhs * input.postedListings);
  site.promoGhs += input.promoGhs;
  if (input.inPool) site.inPool += 1;
}

function hitSlice(hit: MarketplaceHit, period: SharePeriod, atMs: number) {
  const liveListings = hit.listingCount;
  const activity = hit.activity;
  return {
    liveListings,
    postedListings: postedListings(activity, period, atMs),
    allTimePosted: allTimePosted(activity, liveListings),
    liveInventoryGhs: hit.inventoryValueGhs,
    avgPriceGhs: hit.avgPriceGhs,
    paid: hit.paid || hit.premium,
    promoGhs: promoInPeriod(hit.promoSpend.monthlyGhs, hit.promoSpend.lifetimeGhs, period),
  };
}

function subjectSlice(subject: SalesSubject, period: SharePeriod, atMs: number) {
  const liveListings = Math.max(subject.platform?.listingCount ?? 0, subject.activity.listingCount ?? 0);
  const liveInventoryGhs = subject.activity.inventoryValueGhs ?? 0;
  const activity = subjectActivity(subject);
  const monthly = subject.activity.estimatedSpendGhs ?? 0;
  const lifetime = subject.activity.estimatedSpendLifetimeGhs ?? subject.platform?.lifetimePaidGhs ?? 0;
  return {
    liveListings,
    postedListings: postedListings(activity, period, atMs),
    allTimePosted: allTimePosted(activity, liveListings),
    liveInventoryGhs,
    avgPriceGhs: liveListings ? liveInventoryGhs / liveListings : 0,
    paid: (subject.platform?.lifetimePaidGhs ?? 0) > 0,
    promoGhs: promoInPeriod(monthly, lifetime, period),
  };
}

function finishSite(site: SiteShareRow) {
  site.stillLive = stillLiveRatio(site.liveListings, site.allTimePosted);
}

export function buildProspectShare(
  subjects: SalesSubject[],
  vertical: SalesVertical | 'all' = 'all',
  period: SharePeriod = 'all',
  atMs: number = Date.now(),
): ProspectShare {
  const sites = Object.fromEntries(
    SHARE_SITES.map((id) => [id, { id, label: SHARE_SITE_LABEL[id], ...emptyCounts() }]),
  ) as Record<ShareSiteId, SiteShareRow>;

  const ffSubjects = subjects.filter((s) => onPlatform(s) && matchVertical(s.verticals, vertical));
  for (const s of ffSubjects) {
    addSite(sites.feelyfeely, { ...subjectSlice(s, period, atMs), inPool: isInPool(s) });
  }

  const hits = MARKETPLACE_HITS.filter((h) => matchVertical(h.verticals, vertical));
  let overlap = 0;
  let payingElsewhere = 0;
  let payingCaptured = 0;

  for (const hit of hits) {
    const site = sites[hit.marketplace];
    const slice = hitSlice(hit, period, atMs);
    const subject = subjects.find((s) => hitMatchesSubject(hit, s));
    addSite(site, { ...slice, inPool: Boolean(subject && isInPool(subject)) });
    if (subject && onPlatform(subject) && matchVertical(subject.verticals, vertical)) {
      site.alsoOnFf += 1;
      overlap += 1;
    }
    if (slice.paid) {
      payingElsewhere += 1;
      if (subject && onPlatform(subject)) payingCaptured += 1;
    }
  }

  for (const id of SHARE_SITES) finishSite(sites[id]);

  const catMap = new Map<string, Record<ShareSiteId, number>>();
  const bump = (category: string, site: ShareSiteId) => {
    let row = catMap.get(category);
    if (!row) {
      row = Object.fromEntries(SHARE_SITES.map((id) => [id, 0])) as Record<ShareSiteId, number>;
      catMap.set(category, row);
    }
    row[site] += 1;
  };
  for (const s of ffSubjects) {
    if (period !== 'all' && subjectSlice(s, period, atMs).postedListings <= 0) continue;
    for (const c of ffCategories(s)) bump(c, 'feelyfeely');
  }
  for (const hit of hits) {
    if (period !== 'all' && hitSlice(hit, period, atMs).postedListings <= 0) continue;
    for (const c of hit.categories) bump(c, hit.marketplace);
  }

  const categories = [...catMap.entries()]
    .map(([category, bySite]) => ({
      category,
      bySite,
      total: SHARE_SITES.reduce((n, id) => n + bySite[id], 0),
    }))
    .sort((a, b) => b.total - a.total);

  const listingTotal = SHARE_SITES.reduce((n, id) => n + sites[id].postedListings, 0);
  const liveListingTotal = SHARE_SITES.reduce((n, id) => n + sites[id].liveListings, 0);
  const liveInventoryTotal = SHARE_SITES.reduce((n, id) => n + sites[id].liveInventoryGhs, 0);
  const sellerTotal = SHARE_SITES.reduce((n, id) => n + sites[id].sellers, 0);

  return {
    period,
    sites: SHARE_SITES.map((id) => sites[id]),
    categories,
    desks: SALES_VERTICALS.map((desk) => buildDeskSlice(subjects, desk, period, atMs)),
    competitorSellers: hits.length,
    overlap,
    payingElsewhere,
    payingCaptured,
    listingTotal,
    liveListingTotal,
    liveInventoryTotal,
    sellerTotal,
    ffListingShare: listingTotal ? sites.feelyfeely.postedListings / listingTotal : 0,
    ffLiveInventoryShare: liveInventoryTotal ? sites.feelyfeely.liveInventoryGhs / liveInventoryTotal : 0,
    ffSellerShare: sellerTotal ? sites.feelyfeely.sellers / sellerTotal : 0,
    captureRate: hits.length ? overlap / hits.length : 0,
  };
}

function buildDeskSlice(
  subjects: SalesSubject[],
  desk: SalesVertical,
  period: SharePeriod,
  atMs: number,
): DeskShareRow {
  const ff = subjects.filter((s) => onPlatform(s) && s.verticals.includes(desk));
  const hits = MARKETPLACE_HITS.filter((h) => h.verticals.includes(desk));
  return {
    vertical: desk,
    ffSellers: ff.length,
    competitorSellers: hits.length,
    ffListings: ff.reduce((n, s) => n + subjectSlice(s, period, atMs).postedListings, 0),
    competitorListings: hits.reduce((n, h) => n + hitSlice(h, period, atMs).postedListings, 0),
  };
}
